import { useLayoutEffect, useRef, useState } from 'react'

/** Hook for copying content to the clipboard.
 * This is only available on some browsers:
 * https://developer.mozilla.org/en-US/docs/Web/API/Clipboard/write#browser_compatibility
 */
const useCopyToClipboard = (
  data: string,
  didCopyStateMs = 2000,
): [performCopy: () => Promise<void>, didRecentlyCopy: boolean] => {
  const [recentlyCopied, setDidRecentlyCopy] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  useLayoutEffect(() => {
    if (!recentlyCopied) {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
      return
    }

    timeoutRef.current = window.setTimeout(() => {
      setDidRecentlyCopy(false)
    }, didCopyStateMs)

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentlyCopied])

  function write() {
    setDidRecentlyCopy(false)
    return navigator.clipboard.writeText(data).then(() => {
      setDidRecentlyCopy(true)
    })
  }

  return [write, recentlyCopied]
}

export default useCopyToClipboard
